import { CardViewSkeleton } from '@/components/elements/_skeletons/card-view.skeleton';
import { TableViewSkeleton } from '@/components/elements/_skeletons/table-view.skeleton';
import { GeneralCard } from '@/components/elements/general-card/general-card.component';
import { T } from '@/components/i18n/message.i18n.component';
import { GridTableLayout } from '@/components/layout/grid-table/grid-table.layout.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Template, TemplatePublicationStatus } from '@/gql/graphql';
import { useTemplates } from '@/hooks/use-templates/use-templates.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { useUser } from '@/hooks/use-user/use-user.hook';
import { isTemplateDeleteEnabled, isTemplateEditEnabled, isTemplateStatusChangeEnabled } from '@/lib/template.utils';
import { truncateStringLargerThan } from '@/lib/utils';
import { productGroupsPageMessages } from '@/pages/product-groups/product-groups.messages';
import { ProductForm } from '@/pages/products/all/components/product-form/product-form';
import { dialogTranslations, toastTranslations } from '@/pages/products/all/products.messages';
import { useDialogStore } from '@/store/dialog.store';
import { useMutation, useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { allTemplates, deleteTemplate, updateTemplate } from '../templates.requests';
import { templateCardLocators } from './components/template-card.locators';
import { templateCardMessages } from './components/template-card.messages';
import { TemplateForm } from './components/template-form/template-form.component';
import { templateFormMessages } from './components/template-form/template-form.messages';
import { TemplateTable } from './components/template-table.component';
import { templatePageMessages } from './templates.messages';
import { TemplatesNoResults } from './templates.no-results';

export const TemplatesPage = () => {
  const t = useIntl();

  const { guid } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data } = useQuery<{ templates: Template[] }>(allTemplates);

  const {
    templates,
    loading,
    search,
    filter,
    templatesPageView,
    setTemplatesPageView,
    handleFilterChange,
    handleSearchChange,
  } = useTemplates({
    initialTemplates: data?.templates,
  });

  const [editTemplateMutation] = useMutation(updateTemplate);
  const [deleteTemplateMutation] = useMutation(deleteTemplate);

  const { openDialog, closeDialog, setDialogLoading } = useDialogStore((state) => ({
    loading: state.loading,
    openDialog: state.openDialog,
    closeDialog: state.closeDialog,
    setDialogLoading: state.setLoading,
  }));

  const handleTemplateAddDialog = () => {
    openDialog({
      type: 'custom',
      title: <T {...templateFormMessages.addTemplateDialogTitle} />,
      element: <TemplateForm />,
    });
  };

  const handleViewChange = () => {
    setTemplatesPageView(templatesPageView === 'grid' ? 'table' : 'grid');
  };

  const handleNavigate = (id: string) => {
    navigate(`/template/${id}/pre-tailoring`);
  };

  const handleUseAsNewProduct = (template: Template) => {
    openDialog({
      type: 'custom',
      title: <T {...dialogTranslations.addProductTitle} />,
      element: (
        <ProductForm
          context="template"
          initialValues={{
            copyAnswersFrom: template.id,
          }}
          afterAdd={(result) => {
            const id = result.data?.createProduct.id;
            navigate(`/product/${id}/pre-tailoring`);
          }}
        />
      ),
    });
  };

  const handleEdit = (template: Template) => {
    openDialog({
      type: 'custom',
      title: <T {...templateFormMessages.editTemplateDialogTitle} />,
      element: <TemplateForm edit initialValues={template} />,
    });
  };

  const handleStatusChange = (template: Template) => {
    if (template.publicationStatus === TemplatePublicationStatus.Published) {
      openDialog({
        type: 'confirmation',
        title: <T {...dialogTranslations.privateTemplateTitle} />,
        confirmationText: t.formatMessage(dialogTranslations.privateTemplateDescription, {
          name: truncateStringLargerThan(template.name, 40),
        }),
        item: { id: template.id, name: template.name },
        onConfirm: async () => {
          setDialogLoading(true);
          await editTemplateMutation({
            variables: {
              updateTemplateInput: {
                id: template.id,
                name: template.name,
                description: template.description,
                publicationStatus: TemplatePublicationStatus.Private,
              },
            },
            refetchQueries: [{ query: allTemplates }],
          });
          closeDialog();
          toast({
            description: <T {...toastTranslations.privateTemplateToast} />,
            variant: 'default',
          });
        },
      });
    } else {
      openDialog({
        type: 'confirmation',
        title: <T {...dialogTranslations.publishTemplateTitle} />,
        confirmationText: t.formatMessage(dialogTranslations.publishTemplateDescription, {
          name: truncateStringLargerThan(template.name, 40),
        }),
        item: { id: template.id, name: template.name },
        onConfirm: async () => {
          setDialogLoading(true);
          await editTemplateMutation({
            variables: {
              updateTemplateInput: {
                id: template.id,
                name: template.name,
                description: template.description,
                publicationStatus: TemplatePublicationStatus.Published,
              },
            },
            refetchQueries: [{ query: allTemplates }],
          });
          closeDialog();
          toast({
            description: <T {...toastTranslations.publishTemplateToast} />,
            variant: 'default',
          });
        },
      });
    }
  };

  const handleDelete = (template: Template) => {
    openDialog({
      type: 'confirmation',
      item: { id: template.id, name: template.name },
      onConfirm: async () => {
        setDialogLoading(true);
        await deleteTemplateMutation({
          variables: {
            id: template.id,
          },
          refetchQueries: [{ query: allTemplates }],
        });
        closeDialog();
        toast({
          description: <T {...templateFormMessages.deleteTemplateToast} />,
          variant: 'default',
        });
      },
    });
  };

  return (
    <GridTableLayout
      context="templates"
      loading={loading}
      loadingComponent={
        templatesPageView === 'grid' ? (
          <CardViewSkeleton data-testid="template-page-loading-skeleton" />
        ) : (
          <TableViewSkeleton data-testid="template-page-loading-skeleton" />
        )
      }
      noResults={!loading && templates.length === 0}
      noResultsComponent={
        <TemplatesNoResults
          search={search}
          loading={loading}
          templates={templates}
          onButtonClick={handleTemplateAddDialog}
        />
      }
      actionBarConfig={{
        searchPlaceholder: t.formatMessage(templatePageMessages.searchBarPlaceholder),
        filterBarText: <T {...templatePageMessages.filterBarText} />,
        filterBarOptionAllProducts: <T {...templatePageMessages.filterBarOptionBuTemplates} />,
        filterBarOptionMyProducts: <T {...templatePageMessages.filterBarOptionMyTemplates} />,
        tableViewButton: <T {...templatePageMessages.tableViewButton} />,
        gridViewButton: <T {...templatePageMessages.gridViewButton} />,
        selectedView: templatesPageView,
        handleViewChange,
        filter,
        handleFilterChange,
        handleSearchChange,
      }}
      navigationBarConfig={{
        productsTitle: <T {...productGroupsPageMessages.title} />,
        templatesTitle: <T {...templatePageMessages.title} />,
        addButtonTitle: <T {...templatePageMessages.addTemplate} />,
        addButtonAction: handleTemplateAddDialog,
      }}
    >
      {templatesPageView === 'grid' &&
        templates.map((template) => (
          <GeneralCard
            id={template.id}
            key={template.id}
            context="template"
            title={template.name}
            description={template.description}
            onCardClick={handleNavigate}
            button={{
              name: 'use-as-new-product',
              label: <T {...templateCardMessages.useAsNewProduct} />,
              handleClick: () => handleUseAsNewProduct(template),
            }}
            menu={[
              {
                name: 'edit',
                disabled: !isTemplateEditEnabled(guid, template),
                label: <T {...templateCardMessages.editTemplate} />,
                handleClick: () => handleEdit(template),
              },
              {
                name: 'status-change',
                disabled: !isTemplateStatusChangeEnabled(guid, template),
                label:
                  template.publicationStatus === TemplatePublicationStatus.Published ? (
                    <T {...templateCardMessages.privateTemplate} />
                  ) : (
                    <T {...templateCardMessages.publishTemplate} />
                  ),
                handleClick: () => handleStatusChange(template),
                red: template.publicationStatus === TemplatePublicationStatus.Published,
              },
              {
                name: 'delete',
                disabled: !isTemplateDeleteEnabled(guid, template),
                label: <T {...templateCardMessages.deleteTemplate} />,
                handleClick: () => handleDelete(template),
                red: true,
              },
            ]}
            rows={[
              {
                name: 'status',
                label: <T {...templateCardMessages.status} />,
                value: (
                  <Pill
                    data-testid={templateCardLocators.templateCardStatusValue(template.id)}
                    templateStatus={template.publicationStatus}
                    text={template.publicationStatus}
                  />
                ),
              },
              {
                name: 'owner',
                label: <T {...templateCardMessages.owner} />,
                value: template.owner.name,
              },
              {
                name: '-business-unit',
                label: <T {...templateCardMessages.businessUnit} />,
                value: template.businessUnit,
              },
            ]}
          />
        ))}
      {templatesPageView === 'table' && (
        <TemplateTable
          data={templates}
          onRowClick={handleNavigate}
          handleUseAsNewProduct={handleUseAsNewProduct}
          handleStatusChange={handleStatusChange}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
        />
      )}
    </GridTableLayout>
  );
};
